import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isEqual, isObject } from 'lodash';

import {
    DEFAULT_VALUE_KEY,
    FormFieldConfig,
    FormFieldConfigInData,
    FormFieldData,
    GeneralConfig,
    GROUP_SEPARATOR,
} from './useDocumentConfig';
import type { IEventField } from './AssistanceViewContext';
import { useFieldActivity } from './interactionStore';
import { AssistanceFieldContextProvider, useAssistanceFieldContext } from './AssistanceFieldContext';
import FieldWrapper from './customizable/FieldWrapper';
import DynamicField from './DynamicField';
import Field from '../../../core/components/Fields/Field';
import { withIcon } from '../../../core/components/Icon';
import { faExclamationCircle, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import { useConditionalComponent } from '../../../customizations/ConditionalComponentContext';
import { canSelectAcrossPages, fieldIsGreen, fieldIsRed, fieldIsYellow, isFieldValueEmpty } from './utils';
import { useAssistanceContext } from './AssistanceContext';
import ConfidenceExplanation, { useConfidenceExplanationProps } from './ConfidenceExplanation.tsx';
import MatchingExplanation, { useMatchingExplanationProps } from './MatchingExplanation.tsx';
import { useExplanationVisibility } from './Explanation.tsx';
import ValidationCheckExplanation from './ValidationCheckExplanation.tsx';
import {
    filterDisplayedValidationChecks,
    getFieldValidationCheckSeverity,
    hasFailedValidationCheck,
} from './validationChecks.ts';
import { useTranslation } from 'react-i18next';
import { useToaster } from '../../../core/components/Toast.tsx';

export interface AssistanceFieldProps {
    fieldName: string;
    valueKey: string;
    value: any;
    itemIndex?: number;
    itemType?: string;
    fieldIndex?: number;
    placeholder?: string;
    // optional overrides — when omitted, the activity is read from the interaction store
    // (standalone usages like the list-of-services cards manage these themselves)
    isUpdateActive?: boolean;
    isReselectActive?: boolean;
    isFocusActive?: boolean;
    data: FormFieldData & FormFieldConfigInData;
    config: FormFieldConfig;
    generalConfig: GeneralConfig;

    onFocus?: (field: IEventField) => void;
    onBlur?: (field: IEventField) => void;
    onMouseEnter?: (field: IEventField) => void;
    onMouseLeave?: (field: IEventField) => void;
    onAction?: (
        action: IEventField & { action: string; payload?: Record<string, any>; forceRefetchRecord?: boolean }
    ) => Promise<any> | void;
    onReselect?: (reselect: IEventField & { bbox: number[][]; pageIndex: number; isEmailOcr?: boolean }) => void;
    onUpdate?: (update: IEventField & { payload: Record<string, any> }) => Promise<any> | void;
    viewMode?: string;
    documentLoading?: boolean;
    documentReadOnly?: boolean;
    clientPartitionId?: string;
    matchingDocumentNumber?: string;
}

export const LOW_CONFIDENCE_THRESHOLD = 0.4;
export const MEDIUM_CONFIDENCE_THRESHOLD = 0.9;

const LoadingIcon = withIcon(faSpinnerThird);
const EmptyFieldIcon = withIcon(faExclamationCircle);
const ErrorIcon = withIcon(faExclamationCircle);

export const AssistanceFieldControls = ({
    explanationButtonVisible,
    explanationVisibilityProps,
    value,
    confidence,
    severity,
    onIconMouseEnter,
    onIconMouseLeave,
    MatchingExplanationIcon,
    ConfidenceExplanationIcon,
    ValidationCheckIcon,
}: {
    explanationButtonVisible: boolean;
    explanationVisibilityProps: any;
    value: any;
    confidence: number;
    severity: string;
    onIconMouseEnter: any;
    onIconMouseLeave: any;
    MatchingExplanationIcon: any;
    ConfidenceExplanationIcon: any;
    ValidationCheckIcon: any;
}) => {
    const { isRequired, isEmpty, isUpdateActive, isReselectActive, isFocusActive, config, data } =
        useAssistanceFieldContext();
    const isGreen = useMemo(() => fieldIsGreen({ config, value, data }), [config, value, data]);
    const isYellow = useMemo(() => fieldIsYellow({ config, value, data }), [config, value, data]);
    const isRed = useMemo(() => fieldIsRed({ config, value, data }), [config, value, data]);

    const showErrorColors = !isFocusActive && !isUpdateActive && !isReselectActive;

    return isUpdateActive || isReselectActive ? (
        <Field.ControlButton>
            <LoadingIcon className="text-content-brand-strong text-sm animate-spin" />
        </Field.ControlButton>
    ) : explanationButtonVisible ? (
        <Field.ControlButton
            className={classnames(
                showErrorColors && isGreen && '!text-content-success-weak',
                showErrorColors && isYellow && '!text-content-warning-weak',
                showErrorColors && isRed && '!text-content-danger-weak'
            )}
            onClick={() => explanationVisibilityProps.onIsOpenChange(true)}
            onMouseEnter={onIconMouseEnter}
            onMouseLeave={onIconMouseLeave}
        >
            {isEmpty && isRequired ? (
                <EmptyFieldIcon />
            ) : ValidationCheckIcon ? (
                <ValidationCheckIcon />
            ) : MatchingExplanationIcon ? (
                <MatchingExplanationIcon />
            ) : (
                <ConfidenceExplanationIcon />
            )}
        </Field.ControlButton>
    ) : null;
};

export const AssistanceField = ({
    // data from form field
    fieldName,
    valueKey,
    value: propValue,
    itemIndex,
    itemType,
    fieldIndex, // e.g. for header fields where we have multiple fields within a field group
    // data from view state
    placeholder,
    isUpdateActive: isUpdateActiveProp,
    isReselectActive: isReselectActiveProp,
    isFocusActive: isFocusActiveProp,
    // data by reference ideally only used for read-only state
    data,
    config,
    generalConfig,
    // event handlers
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onAction,
    onReselect,
    onUpdate,
    // more stuff
    documentLoading = false,
    documentReadOnly = false,
    clientPartitionId,
    matchingDocumentNumber,
}: AssistanceFieldProps) => {
    const fullFieldName =
        valueKey && valueKey !== DEFAULT_VALUE_KEY ? `${fieldName}${GROUP_SEPARATOR}${valueKey}` : fieldName;

    const fieldActivity = useFieldActivity(fieldName, fullFieldName, itemIndex);
    const isUpdateActive = isUpdateActiveProp ?? fieldActivity.isUpdateActive;
    const isReselectActive = isReselectActiveProp ?? fieldActivity.isReselectActive;
    const isFocusActive = isFocusActiveProp ?? fieldActivity.isFocusActive;

    const { documentConfiguration } = useAssistanceContext();

    const inputRef = useRef(null);
    const wrapperRef = useRef(null);

    /* VALUE MANAGEMENT */

    // TODO: double check if date fields should be nullable
    const defaultValue = propValue == null ? '' : propValue; // prevent field to become uncontrolled
    const [fieldValue, setFieldValue] = useState(defaultValue);

    const [updatePayload, setUpdatePayload] = useState({});

    const [isUpdated, setIsUpdated] = useState(false);
    const [isInvalid, setIsInvalid] = useState(false);

    const isUpdatedTimeoutRef = useRef(null);
    useEffect(() => () => clearTimeout(isUpdatedTimeoutRef.current), []);

    const isRequired = config?.isRequired || false;
    const isEmpty = isFieldValueEmpty(fieldValue) && isFieldValueEmpty(defaultValue);

    // the last commit sent via onUpdate that the server has not confirmed yet (defaultValue
    // still holds the old value until the patch lands); swallows duplicate commits, e.g. a
    // select-type field firing on option select and again on its real blur
    const lastCommitRef = useRef(null);

    useEffect(() => {
        // triggered by external update — server confirmed or external change, new baseline
        lastCommitRef.current = null;

        if (!isEqual(defaultValue, fieldValue)) {
            setFieldValue(defaultValue);

            if (fieldValue != null) {
                // only show updated if previous value existed
                setIsUpdated(true);
                clearTimeout(isUpdatedTimeoutRef.current);
                isUpdatedTimeoutRef.current = setTimeout(() => setIsUpdated(false), 1000);
            }
        }
    }, [defaultValue]);

    const handleValueChange = (value: any) => {
        setFieldValue(value);
    };

    const handleUpdatePayloadChange = (updatePayload: any) => {
        setUpdatePayload(updatePayload);
    };

    const { publishToast } = useToaster();
    const { t } = useTranslation('assistance');
    // the only commit path: fields signal finalized input via onCommit with the final value
    // as an argument; lookups attach their updatePayload, everything else falls back to the
    // payload state fed through onUpdatePayloadChange
    const handleCommit = (value: any, payload = updatePayload) => {
        // prevent unnecessary updates
        if (value === defaultValue && isFieldValueEmpty(payload)) return;

        // swallow duplicate commits while one is still in flight (e.g. a select-type field
        // firing on option select and again on its real blur): until the server patch
        // updates defaultValue, the first guard above cannot catch these
        if (lastCommitRef.current && isEqual(lastCommitRef.current.value, value)) {
            if (isFieldValueEmpty(payload)) return;
            if (isEqual(lastCommitRef.current.payload, payload)) return;
        }

        // if value is an object we can ignore the valueKey
        const valuePayload = isObject(value) ? value : { [valueKey]: value };

        // prevent update with input value if reselect is active
        if (isReselectActive) return;

        lastCommitRef.current = { value, payload };

        Promise.resolve(
            onUpdate?.({
                fieldName: fieldName,
                itemIndex,
                itemType,
                payload: { ...payload, ...valuePayload },
            })
        )
            // rollback changes to the default value if the update is failed
            .catch(() => {
                lastCommitRef.current = null;
                setFieldValue(defaultValue);
                publishToast({
                    description: t('assistance.updateFieldError', { field: placeholder || fieldName }),
                    status: 'error',
                });
            });

        setUpdatePayload({});
    };

    /* CONFIDENCE AND DOCUMENT MATCHING EXPLANATIONS */

    const confidence = data?.predictionConfidence || 0;
    const confidenceExplanation: any = data?.confidenceExplanation || {};
    const dateFormat = generalConfig?.dateFormat || 'L';

    const confidenceExplanationProps = useConfidenceExplanationProps(
        confidenceExplanation,
        dateFormat,
        fieldName,
        data.__typename
    );

    const matchingExplanationProps = useMatchingExplanationProps(
        confidenceExplanation,
        dateFormat,
        isRequired,
        isFieldValueEmpty(defaultValue)
    );

    const makeFieldAction = (action: string, extra?: Record<string, any>) => () => {
        return onAction?.({
            fieldName: fieldName,
            itemIndex,
            itemType,
            action,
            ...extra,
        });
    };

    const handleAcceptDeviation = makeFieldAction(`action:${matchingExplanationProps?.acceptActionKey}`);
    const handleUseCatalogPrice = makeFieldAction('action:use_catalog_price');

    const validationChecks = filterDisplayedValidationChecks(data?.validationChecks);

    /* FIELD EVENT HANDLERS */

    const explanationVisibilityProps = useExplanationVisibility({ wrapperRef, isFocusActive });

    const explanationButtonVisible =
        confidenceExplanationProps?.message || matchingExplanationProps?.message || validationChecks.length > 0;

    /* FIELD EVENT HANDLERS */

    const handleFocus = (e) => {
        explanationVisibilityProps.onFieldFocus(e);
        return onFocus?.({
            fieldName: fullFieldName,
            itemIndex,
            itemType,
            reselectTargetRef: inputRef,
            canSelectAcrossPages: canSelectAcrossPages(data, config, valueKey),
        });
    };

    const handleBlur = (e) => {
        explanationVisibilityProps.onFieldBlur(e);
        return onBlur?.({ fieldName: fullFieldName, itemIndex, itemType, reselectTargetRef: inputRef });
    };

    const handleMouseEnter = (e) => {
        explanationVisibilityProps.onFieldMouseEnter(e);
        return onMouseEnter?.({ fieldName: fullFieldName, itemIndex, itemType, reselectTargetRef: inputRef });
    };

    const handleMouseLeave = (e) => {
        explanationVisibilityProps.onFieldMouseLeave(e);
        return onMouseLeave?.({ fieldName: fullFieldName, itemIndex, itemType, reselectTargetRef: inputRef });
    };

    const handleMouseOut = (e) => {
        explanationVisibilityProps.onFieldMouseOut(e);
    };

    const handleIconMouseEnter = (e) => {
        return explanationVisibilityProps.onIconMouseEnter(e);
    };

    const handleIconMouseLeave = (e) => {
        return explanationVisibilityProps.onIconMouseLeave(e);
    };

    const handleReselect = (e) => {
        return onReselect?.({
            fieldName: fullFieldName,
            itemIndex,
            itemType,
            bbox: e.detail.bbox,
            pageIndex: e.detail.pageIndex,
            isEmailOcr: Boolean(e.detail.isEmailOcr),
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            inputRef.current?.blur();
        }
    };

    const handleReset = makeFieldAction('action:reset_field', { forceRefetchRecord: true });

    /* PROP EXTENDABILITY */

    const FieldWrapperComponent = useConditionalComponent(
        'FieldWrapper',
        {
            fieldName: fullFieldName,
            itemIndex,
            itemType,
        },
        FieldWrapper
    );

    const FieldComponent = useConditionalComponent(
        'Field',
        {
            fieldName: fullFieldName,
            itemIndex,
            itemType,
            documentTypeName: documentConfiguration?.documentTypeName,
            config,
        },
        DynamicField
    );

    return (
        <AssistanceFieldContextProvider
            fieldIndex={fieldIndex}
            fieldName={fieldName}
            fullFieldName={fullFieldName}
            value={fieldValue}
            data={data}
            config={config}
            isRequired={isRequired}
            isEmpty={isEmpty}
            isLoading={documentLoading || isUpdateActive || isReselectActive}
            isUpdated={isUpdated}
            isInvalid={isInvalid}
            isUpdateActive={isUpdateActive}
            isReselectActive={isReselectActive}
            isFocusActive={isFocusActive}
            isHoverActive={undefined}
            clientPartitionId={clientPartitionId}
            matchingDocumentNumber={matchingDocumentNumber}
            itemIndex={itemIndex}
            itemType={itemType}
        >
            <FieldWrapperComponent
                fieldName={fieldName}
                fullFieldName={fullFieldName}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseOut={handleMouseOut}
                confidenceExplanationButtonVisible={!!explanationButtonVisible}
                isConfidenceExplanationOpen={explanationVisibilityProps.isOpen}
                confidenceExplanationAlignment={explanationVisibilityProps.alignment}
                field={
                    <FieldComponent
                        placeholder={placeholder}
                        value={fieldValue}
                        onValueChange={handleValueChange}
                        onUpdatePayloadChange={handleUpdatePayloadChange}
                        onCommit={handleCommit}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        onReselect={handleReselect}
                        onReset={handleReset}
                        confidence={confidence}
                        severity={matchingExplanationProps?.severity}
                        // hasFailedValidationChecks={true}
                        documentLoading={documentLoading}
                        documentReadOnly={documentReadOnly}
                        inputRef={inputRef}
                        controls={
                            <AssistanceFieldControls
                                explanationButtonVisible={!!explanationButtonVisible}
                                explanationVisibilityProps={explanationVisibilityProps}
                                value={fieldValue}
                                confidence={confidence}
                                severity={matchingExplanationProps?.severity}
                                onIconMouseEnter={handleIconMouseEnter}
                                onIconMouseLeave={handleIconMouseLeave}
                                MatchingExplanationIcon={matchingExplanationProps?.Icon}
                                ConfidenceExplanationIcon={confidenceExplanationProps?.Icon}
                                ValidationCheckIcon={
                                    hasFailedValidationCheck(data?.validationChecks) ? ErrorIcon : undefined
                                }
                            />
                        }
                    />
                }
                confidenceExplanation={
                    <div className="flex flex-col overflow-hidden">
                        {confidenceExplanationProps.message && (
                            <ConfidenceExplanation {...confidenceExplanationProps} />
                        )}

                        {matchingExplanationProps.message && (
                            <MatchingExplanation {...matchingExplanationProps} onAccept={handleAcceptDeviation} />
                        )}

                        {validationChecks.map((validationCheck, index) => (
                            <ValidationCheckExplanation
                                key={index}
                                validationCheck={validationCheck}
                                fieldName={fieldName}
                                severity={getFieldValidationCheckSeverity(validationCheck)}
                                showIcon={false}
                                onAction={config?.allowUseCatalogPrice ? handleUseCatalogPrice : undefined}
                            />
                        ))}
                    </div>
                }
                ref={wrapperRef}
            />
        </AssistanceFieldContextProvider>
    );
};

// Memo boundary for render sites that map over many fields (header/item sections); only
// effective while the field group references (data/config) stay stable between renders
export const MemoAssistanceField = React.memo(AssistanceField);
